import type { ReactNode } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export interface AdminDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  footer?: ReactNode;
  destructive?: boolean;
}

/**
 * Reusable centred modal used for all create/edit/confirm forms across
 * every admin panel.
 *
 * Slots:
 *  - `children`  — the form body (scrollable)
 *  - `footer`    — action buttons rendered in the DialogActions row
 *
 * The caller is responsible for providing Cancel / Submit buttons in `footer`.
 * Clicking the × button calls `onClose` — same as clicking outside (backdrop).
 */
export function AdminDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = "sm",
  footer,
  destructive = false,
}: AdminDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2 } } }}
    >
      {/* Header */}
      <DialogTitle
        component="div"
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: destructive ? "error.50" : "grey.50",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.25,
              color: destructive ? "error.dark" : "text.primary",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{ mt: -0.25, flexShrink: 0, color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Body */}
      <DialogContent sx={{ px: 3, py: 3 }}>{children}</DialogContent>

      {/* Footer */}
      {footer && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>{footer}</DialogActions>
        </>
      )}
    </Dialog>
  );
}
